import { useTranslation } from 'react-i18next';
import { Link, useSearchParams } from 'react-router-dom';
import moment from 'moment';
import { SparklesIcon } from '@heroicons/react/24/outline';
import classNames from 'classnames';
import { useMutation } from '@apollo/client';
import { UPDATE_MANUAL_MODE } from '~/services';
import { useState } from 'react';
import { motion } from 'motion/react';

// Utility function to determine text color based on background brightness
const getContrastTextColor = (hexColor: string) => {
  // Remove # if present
  const color = hexColor.replace('#', '');

  // Convert to RGB
  const r = parseInt(color.substr(0, 2), 16);
  const g = parseInt(color.substr(2, 2), 16);
  const b = parseInt(color.substr(4, 2), 16);

  // Calculate luminance
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;

  // Return dark text for light backgrounds, light text for dark backgrounds
  return luminance > 0.5 ? '#1f2937' : '#ffffff';
};

// Utility function to get styles for tag colors
const getTagStyles = (color: string) => {
  // Handle named colors or fallback
  if (!color || color === 'gray' || color === 'grey') {
    return {
      backgroundColor: '#e5e7eb',
      color: '#1f2937',
      className: 'font-semibold',
    };
  }

  // For hex colors, use the actual color
  if (color.startsWith('#')) {
    return {
      backgroundColor: color,
      color: getContrastTextColor(color),
      className: 'font-semibold',
    };
  }

  // For other color formats, try to use them directly
  return {
    backgroundColor: color,
    color: getContrastTextColor('#808080'), // Default contrast for unknown formats
    className: 'font-semibold',
  };
};

interface ConversationCardProps {
  name: string | null;
  lastUpdate: string;
  personId: string;
  manualMode: boolean;
  activityId: string;
  tags: { color: string; name: string }[];
}

export const ConversationCard = ({
  name,
  personId,
  lastUpdate,
  manualMode = false,
  activityId,
  tags = [],
}: ConversationCardProps) => {
  const [searchParams] = useSearchParams();
  const pathParts = window.location.pathname.split('/');
  const conversationId = pathParts[4];
  const [updateManualMode, { loading }] = useMutation(UPDATE_MANUAL_MODE, {
    onCompleted: () => {
      _setManualMode(!_manualMode);
    },
  });
  const [_manualMode, _setManualMode] = useState(manualMode);
  const { t } = useTranslation('conversations');

  const isSelected = conversationId === personId;

  return (
    <motion.div
      className={`bg-base-100 flex h-24 w-full text-sm ${
        isSelected ? 'text-primary' : 'text-base-content'
      }`}
      initial={{ opacity: 0 }}
      animate={{
        opacity: 1,
        transition: {
          delay: 0.1,
          duration: 0.3,
        },
      }}
      whileHover={{
        backgroundColor: isSelected ? undefined : 'hsl(var(--bc) / 0.05)',
        transition: { duration: 0.2 },
      }}
    >
      <Link
        to={{
          pathname: personId,
          search: `${
            searchParams.get('search')
              ? `?search=${searchParams.get('search')}`
              : ''
          }`,
        }}
        data-testid="card-action-button"
        className="flex-1"
      >
        <motion.div
          className="bg-base-100 flex h-full w-full justify-between pb-2 pl-3 pr-3 pt-3"
          initial={{ opacity: 0, x: -10 }}
          animate={{
            opacity: 1,
            x: 0,
            transition: {
              delay: 0.15,
              type: 'spring',
              stiffness: 300,
              damping: 25,
            },
          }}
        >
          {/* Left Column: Person Info */}
          <div className="flex flex-1 flex-col gap-1">
            <motion.h3
              className="text-base-content font-semibold"
              initial={{ opacity: 0, y: -5 }}
              animate={{
                opacity: 1,
                y: 0,
                transition: {
                  delay: 0.2,
                  duration: 0.3,
                },
              }}
            >
              {name || t('unknown')}
            </motion.h3>

            <motion.p
              className="text-base-content text-xs opacity-70"
              initial={{ opacity: 0 }}
              animate={{
                opacity: 1,
                transition: {
                  delay: 0.25,
                  duration: 0.3,
                },
              }}
            >
              {moment(lastUpdate).format('lll')}
            </motion.p>

            <motion.div
              className="mt-1 flex gap-1"
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{
                opacity: 1,
                scale: 1,
                transition: {
                  delay: 0.3,
                  type: 'spring',
                  stiffness: 400,
                  damping: 20,
                },
              }}
            >
              {tags
                .filter(
                  (tag, index, self) =>
                    index === self.findIndex((t) => t.name === tag.name),
                )
                .slice(0, 2)
                .map((tag, index) => {
                  const tagStyles = getTagStyles(tag.color ?? 'gray');
                  return (
                    <motion.div
                      key={tag.name}
                      className={`h-fit w-fit max-w-[10em] truncate rounded-full px-2 py-1 text-[10px] md:max-w-[9em] ${tagStyles.className}`}
                      style={{
                        backgroundColor: tagStyles.backgroundColor,
                        color: tagStyles.color,
                      }}
                      initial={{ opacity: 0, scale: 0.6, y: -5 }}
                      animate={{
                        opacity: 1,
                        scale: 1,
                        y: 0,
                        transition: {
                          delay: 0.35 + index * 0.05,
                          type: 'spring',
                          stiffness: 500,
                          damping: 25,
                        },
                      }}
                      whileHover={{
                        scale: 1.1,
                        y: -2,
                        transition: { duration: 0.2 },
                      }}
                    >
                      {tag.name}
                    </motion.div>
                  );
                })}
            </motion.div>
          </div>

          {/* Right Column: AI Button */}
          <div className="flex">
            <motion.button
              disabled={loading}
              className={classNames(
                'tooltip tooltip-left border-1 h-6 w-6 cursor-pointer rounded-full border p-1 transition-all duration-200',
                {
                  'bg-base-200 border-primary': _manualMode,
                  'bg-success border-success': !_manualMode,
                },
              )}
              data-tip={
                _manualMode
                  ? t('conversations-tooltip-enabled')
                  : t('conversations-tooltip-disabled')
              }
              onClick={() => {
                updateManualMode({
                  variables: {
                    data: {
                      id: activityId,
                      state: !_manualMode,
                    },
                  },
                });
              }}
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{
                opacity: 1,
                scale: 1,
                transition: {
                  delay: 0.4,
                  type: 'spring',
                  stiffness: 400,
                  damping: 20,
                },
              }}
              whileHover={{
                scale: 1.1,
                transition: { duration: 0.2 },
              }}
              whileTap={{
                scale: 0.95,
                transition: { duration: 0.1 },
              }}
            >
              <motion.div
                animate={{
                  rotate: loading ? 360 : 0,
                  transition: {
                    duration: loading ? 1 : 0.3,
                    repeat: loading ? Infinity : 0,
                    ease: loading ? 'linear' : 'easeOut',
                  },
                }}
              >
                <SparklesIcon
                  className={classNames('h-4 w-4', {
                    'text-primary': _manualMode,
                    'text-success-content': !_manualMode,
                  })}
                />
              </motion.div>
            </motion.button>
          </div>
        </motion.div>
      </Link>
    </motion.div>
  );
};
